/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import * as Sentry from "@sentry/react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, functions, db } from "../firebase";
import { syncUserEmail, updateUserLastActive } from "../services/userService";
import { identifyPostHogUser } from "../services/posthogService";
import { handleSnapshotError } from "../utils/recoveryErrors";
import { reportError } from "../utils/reportError";
import { getFunctionsErrorCode } from "../utils/callableErrors";
import {
  isTransientClaimError,
  runWithTransientRetry,
} from "../utils/claimLinkErrorPolicy";
import { isPageUnloading } from "../utils/pageLifecycle";

/**
 * Response from claimCustomerAndOnboardingLink Cloud Function
 */
interface ClaimLinkResponse {
  linked: boolean;
  customerId?: string;
  onboardingId?: string;
  reason?: string;
}

/**
 * Response from initializeNewUser Cloud Function
 */
interface InitializeNewUserResponse {
  success: boolean;
  userId: string;
  userCreated: boolean;
  walletCreated: boolean;
  initialTokens: number;
  isPremium: boolean;
  reason?: string;
}

/**
 * Auth context state
 */
interface AuthContextState {
  /** The Firebase Auth user object, null if not authenticated */
  user: User | null;
  /** Whether auth state is still being determined */
  loading: boolean;
  /** Whether the user has been linked to a customer record */
  isLinked: boolean;
  /** The linked customer ID, if any */
  customerId: string | null;
  /** The linked onboarding ID, if any */
  onboardingId: string | null;
  /** Error message from linking attempt, if any */
  linkError: string | null;
  /** Whether linking is in progress */
  isLinking: boolean;
  /** Manually trigger the claim link function */
  claimLink: () => Promise<ClaimLinkResponse | null>;
}

const AuthContext = createContext<AuthContextState | undefined>(undefined);

// Reasons a retry cannot help with.
//
// `customer_linked_to_other_uid` and `multiple_matches` used to be here and are
// deliberately gone: the server now resolves several customers per email
// deterministically, and recovers a customer bound to a stale uid belonging to
// the same verified email. Keeping them here made those situations a permanent
// premium lockout — the user could never re-attempt the claim.
const NON_RETRIABLE_REASONS = [
  "already_linked_different_customer",
  "onboarding_linked_to_other_uid",
];

// Storage key prefix for tracking link attempts.
//
// Namespaced PER UID: a single global key was only cleared when the Firebase
// user became null, which an in-place anonymous upgrade or a
// signInWithCustomToken never does. One "linked" or non-retriable verdict from
// a previous identity therefore short-circuited the claim for every later
// account in the same tab.
const LINK_ATTEMPTED_KEY_PREFIX = "linkAttempted";

function linkAttemptedKey(uid: string): string {
  return `${LINK_ATTEMPTED_KEY_PREFIX}:${uid}`;
}

// A soft, time-boxed backoff for the "this user simply has no customer record"
// answer — distinct from NON_RETRIABLE_REASONS, which is permanent. Most users
// never purchase, so without a backoff the claim runs on every page load
// forever; with one that expires, a later purchase still links.
const NO_CUSTOMER_MATCH_KEY_PREFIX = "noCustomerMatch";
const NO_CUSTOMER_MATCH_BACKOFF_MS = 6 * 60 * 60 * 1000;

function noCustomerMatchKey(uid: string): string {
  return `${NO_CUSTOMER_MATCH_KEY_PREFIX}:${uid}`;
}

function isRecentNoCustomerMatch(uid: string): boolean {
  try {
    const raw = window.localStorage.getItem(noCustomerMatchKey(uid));
    if (!raw) {
      return false;
    }
    return Date.now() - Number(raw) < NO_CUSTOMER_MATCH_BACKOFF_MS;
  } catch {
    return false;
  }
}

function rememberNoCustomerMatch(uid: string): void {
  try {
    window.localStorage.setItem(noCustomerMatchKey(uid), String(Date.now()));
  } catch {
    // Private-mode storage failures just mean we retry next load.
  }
}

function clearNoCustomerMatch(uid: string): void {
  try {
    window.localStorage.removeItem(noCustomerMatchKey(uid));
  } catch {
    // Ignore.
  }
}

function isEmailLinkAuthenticatedUser(authUser: User | null): boolean {
  return !!authUser && !authUser.isAnonymous;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLinked, setIsLinked] = useState(false);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [onboardingId, setOnboardingId] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  // Track which UID we've already attempted to initialize in this session
  const initializedUidRef = useRef<string | null>(null);
  // Guards the "user doc exists but has no customerId" claim so a chatty
  // snapshot listener triggers it once per uid, not once per snapshot.
  const claimedUidRef = useRef<string | null>(null);
  // Shares one in-flight claim between the snapshot-triggered call and
  // VerifyEmailPage's explicit one — the transient-failure retry stretches a
  // claim to ~2.5s, long enough for the two to overlap.
  const claimInFlightRef = useRef<Promise<ClaimLinkResponse | null> | null>(
    null
  );
  const lastActivityPingRef = useRef<number>(0);

  /**
   * Initialize new user (create users and wallets documents)
   */
  const initializeUser = useCallback(async (): Promise<boolean> => {
    if (!user) {
      console.log("initializeUser: No user, skipping");
      return false;
    }

    try {
      const initFunction = httpsCallable<
        { preferredLanguage?: string },
        InitializeNewUserResponse
      >(
        functions,
        "initializeNewUser"
      );

      const storedLanguage =
        typeof window !== "undefined"
          ? window.localStorage.getItem("preferredLanguage") ?? undefined
          : undefined;
      const browserLanguage =
        typeof navigator !== "undefined" ? navigator.language : "en";
      const preferredLanguage =
        storedLanguage ??
        (browserLanguage.toLowerCase().startsWith("de") ? "de" : "en");

      const result = await initFunction({ preferredLanguage });
      const response = result.data;

      console.log("initializeUser response:", response);

      if (response.success) {
        console.log(
          `User initialized: userCreated=${response.userCreated}, ` +
            `walletCreated=${response.walletCreated}, ` +
            `initialTokens=${response.initialTokens}, ` +
            `isPremium=${response.isPremium}`
        );
        return true;
      }

      return false;
    } catch (error) {
      const code = getFunctionsErrorCode(error);
      const transient = isTransientClaimError(error);

      if (transient && isPageUnloading()) {
        // Killed by a hard navigation (FinishSignIn's window.location.replace
        // fires right after sign-in). Initialization re-runs on the next page
        // load — not an error.
        console.warn("initializeUser aborted by page unload; will retry after reload");
        return false;
      }

      console.error("initializeUser error:", error);
      reportError(error, {
        area: "auth",
        surface: "auth_provider",
        op: "initialize_user",
        level: transient ? "warning" : "error",
        tags: {
          ...(code ? { code } : {}),
          transient: String(transient),
        },
        extra: { uid: user?.uid },
      });
      // Don't throw - initialization failure shouldn't block authentication
      return false;
    }
  }, [user]);

  /**
   * One claim attempt against the claimCustomerAndOnboardingLink Cloud
   * Function, including transient-failure retries and error reporting.
   */
  const claimLinkAttempt = useCallback(
    async (currentUser: User): Promise<ClaimLinkResponse | null> => {
      setIsLinking(true);
      setLinkError(null);

      try {
        const claimFunction = httpsCallable<void, ClaimLinkResponse>(
          functions,
          "claimCustomerAndOnboardingLink"
        );

        const result = await runWithTransientRetry(() => claimFunction(), {
          isAborting: isPageUnloading,
        });
        const response = result.data;

        console.log("claimLink response:", response);

        if (response.linked) {
          setIsLinked(true);
          setCustomerId(response.customerId || null);
          setOnboardingId(response.onboardingId || null);
          setLinkError(null);
          // Mark as successfully linked
          sessionStorage.setItem(linkAttemptedKey(currentUser.uid), "linked");
          clearNoCustomerMatch(currentUser.uid);
        } else {
          setIsLinked(false);
          setLinkError(response.reason || "Unknown error");
          if (response.reason === "no_customer_match") {
            // Expected for every user who has never purchased — back off rather
            // than re-asking on each page load.
            rememberNoCustomerMatch(currentUser.uid);
          }
          // Mark non-retriable errors
          if (response.reason && NON_RETRIABLE_REASONS.includes(response.reason)) {
            sessionStorage.setItem(
              linkAttemptedKey(currentUser.uid),
              response.reason
            );
          }
        }

        return response;
      } catch (error) {
        const code = getFunctionsErrorCode(error);
        const transient = isTransientClaimError(error);

        if (transient && isPageUnloading()) {
          // Killed by a hard navigation (FinishSignIn's
          // window.location.replace fires right after sign-in). Sign-in
          // already succeeded; the claim re-runs on the next page load — not
          // an error.
          console.warn("claimLink aborted by page unload; will retry after reload");
          return null;
        }

        console.error("claimLink error:", error);
        if (transient && claimedUidRef.current === currentUser.uid) {
          // Let a later user-doc snapshot in this SPA session re-attempt the
          // claim. Transient-only: a genuinely broken server must not be
          // re-hit on every snapshot.
          claimedUidRef.current = null;
        }
        reportError(error, {
          area: "auth",
          surface: "auth_provider",
          op: "claim_link",
          level: transient ? "warning" : "error",
          tags: {
            ...(code ? { code } : {}),
            transient: String(transient),
          },
          extra: {
            uid: currentUser.uid,
            message: error instanceof Error ? error.message : String(error),
          },
        });
        const errorMessage =
          error instanceof Error ? error.message : "Failed to link account";
        setLinkError(errorMessage);
        return null;
      } finally {
        setIsLinking(false);
      }
    },
    []
  );

  /**
   * Call the claimCustomerAndOnboardingLink Cloud Function
   */
  const claimLink = useCallback(async (): Promise<ClaimLinkResponse | null> => {
    if (!user) {
      console.log("claimLink: No user, skipping");
      return null;
    }
    if (user.isAnonymous) {
      console.log("claimLink: Anonymous user, skipping");
      return null;
    }

    if (claimInFlightRef.current) {
      return claimInFlightRef.current;
    }

    const attempt = claimLinkAttempt(user);
    claimInFlightRef.current = attempt;
    try {
      return await attempt;
    } finally {
      claimInFlightRef.current = null;
    }
  }, [user, claimLinkAttempt]);

  /**
   * Listen for auth state changes
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (import.meta.env.DEV && firebaseUser?.uid) {
        console.log("Auth state changed:", firebaseUser.uid);
      }

      if (!firebaseUser) {
        setUser(null);
        Sentry.setUser(null);
        setIsLinked(false);
        setCustomerId(null);
        setOnboardingId(null);
        setLinkError(null);
        initializedUidRef.current = null;
        claimedUidRef.current = null;
        setLoading(false);
        return;
      }

      const authenticatedUser = firebaseUser as User;

      // Check if this is a new authentication (different user or first time)
      const isNewAuth = initializedUidRef.current !== authenticatedUser.uid;
      if (isNewAuth) {
        console.log("New user authenticated:", authenticatedUser.uid);
        // Don't set initializedUidRef here - it will be set after successful init
      }

      setUser(authenticatedUser);
      identifyPostHogUser(authenticatedUser);
      // Unlike PostHog, Sentry gets the uid for anonymous users too — errors
      // in the pre-auth funnel are only cross-referenceable by that uid.
      Sentry.setUser({
        id: authenticatedUser.uid,
        ...(authenticatedUser.email ? { email: authenticatedUser.email } : {}),
      });
      if (!isEmailLinkAuthenticatedUser(authenticatedUser)) {
        setIsLinked(false);
        setCustomerId(null);
        setOnboardingId(null);
        setLinkError(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    const pingActivity = () => {
      const now = Date.now();
      if (now - lastActivityPingRef.current < 30000) {
        return;
      }
      lastActivityPingRef.current = now;
      void updateUserLastActive(user.uid);
    };

    pingActivity();

    const intervalId = window.setInterval(pingActivity, 60000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        pingActivity();
      }
    };

    window.addEventListener("focus", pingActivity);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", pingActivity);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user]);

  // `users/{uid}.email` stays client-writable: the onboarding quiz records an
  // email while the visitor is still anonymous, and the idle re-engagement
  // scheduler mails that address. It is therefore NOT proof of anything — no
  // server code may make a trust decision from it. The two places that used to
  // (customer linking and account deletion) now read the verified Auth record
  // instead.
  useEffect(() => {
    if (!user || user.isAnonymous || !user.email) {
      return;
    }

    syncUserEmail(user.uid, user.email).catch((error) => {
      console.warn("Failed to sync authenticated user email:", error);
    });
  }, [user]);

  /**
   * Proactively refresh the Firebase ID token when the tab regains visibility.
   * Firebase ID tokens expire after 1 hour; if the SDK's background refresh
   * failed (e.g. due to third-party cookie restrictions or prolonged idle),
   * this ensures the session is recovered before making any API calls.
   */
  useEffect(() => {
    if (!user) {
      return;
    }

    const lastRefreshRef = { ts: Date.now() };
    const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

    const refreshTokenOnReturn = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const elapsed = Date.now() - lastRefreshRef.ts;
      if (elapsed < TOKEN_REFRESH_THRESHOLD_MS) {
        return;
      }
      lastRefreshRef.ts = Date.now();
      user.getIdToken(true).catch((err) => {
        console.warn("Proactive token refresh failed:", err);
      });
    };

    document.addEventListener("visibilitychange", refreshTokenOnReturn);
    return () =>
      document.removeEventListener("visibilitychange", refreshTokenOnReturn);
  }, [user]);

  /**
   * Listen for user document changes to track linking status
   * This provides real-time updates when linking happens in the backend
   */
  useEffect(() => {
    if (!user) {
      return;
    }

    const userDocRef = doc(db, "users", user.uid);

    const unsubscribe = onSnapshot(
      userDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const userData = snapshot.data();
          const hasCustomerId = !!userData.customerId;

          setIsLinked(hasCustomerId);
          setCustomerId(userData.customerId || null);
          setOnboardingId(userData.onboardingId || null);

          if (hasCustomerId) {
            setLinkError(null);
            sessionStorage.setItem(linkAttemptedKey(user.uid), "linked");
            console.log(`User ${user.uid} linked to customer ${userData.customerId}`);
            return;
          }

          // The document exists but carries no customerId.
          //
          // This branch used to do nothing, and it is the direct cause of
          // "signed in with Google and my subscription was gone": an anonymous
          // visitor already HAS a users/{uid} document (the onboarding quiz
          // writes one), so after an in-place upgrade — linkWithPopup,
          // linkWithCredential, or a magic link that reused the anonymous uid —
          // the doc-missing branch below never fires and the customer claim was
          // never attempted at all.
          if (user.isAnonymous) {
            return;
          }
          if (claimedUidRef.current === user.uid) {
            return;
          }

          const previousVerdict = sessionStorage.getItem(
            linkAttemptedKey(user.uid)
          );
          if (
            previousVerdict &&
            NON_RETRIABLE_REASONS.includes(previousVerdict)
          ) {
            setLinkError(previousVerdict);
            return;
          }

          // `no_customer_match` is the normal answer for every free user, and
          // `claimedUidRef` only survives the current mount — without this the
          // largest segment would fire an auth lookup plus a customers query
          // plus a transaction on every single page load, forever. Held in
          // localStorage, but time-boxed and cleared on a successful claim, so
          // a later purchase still links: the doc-missing branch, an explicit
          // claimLink() after verification, and the server-side wallet trigger
          // all remain available in the meantime.
          if (isRecentNoCustomerMatch(user.uid)) {
            return;
          }

          claimedUidRef.current = user.uid;
          void claimLink();
        } else {
          // User document doesn't exist yet
          setIsLinked(false);

          // Only initialize if we haven't already attempted initialization for this UID
          // This ensures we only initialize for NEW authentications, not if the document
          // was deleted or in race conditions
          if (initializedUidRef.current === user.uid) {
            // Already attempted initialization for this UID, skip
            console.log(
              "Already attempted initialization for this user, skipping"
            );
            return;
          }

          // Check if we should attempt to initialize and link
          const previousAttempt = sessionStorage.getItem(
            linkAttemptedKey(user.uid)
          );

          if (previousAttempt === "linked") {
            // Already attempted, don't retry
            return;
          }

          if (previousAttempt && NON_RETRIABLE_REASONS.includes(previousAttempt)) {
            // Previous non-retriable error
            console.log("Previous non-retriable error:", previousAttempt);
            setLinkError(previousAttempt);
            return;
          }

          // Mark that we're attempting initialization for this user
          // This prevents retries if the snapshot fires multiple times
          const initKey = `init_${user.uid}`;
          if (sessionStorage.getItem(initKey)) {
            console.log("Initialization already in progress for this user");
            return;
          }
          sessionStorage.setItem(initKey, "attempting");

          // Mark that we're attempting initialization for this UID
          initializedUidRef.current = user.uid;

          // Initialize user first, then attempt to link
          setTimeout(async () => {
            try {
              // Initialize user (creates users and wallets documents)
              const initSuccess = await initializeUser();
              if (initSuccess) {
                // Then attempt to claim customer link
                if (!user.isAnonymous) {
                  await claimLink();
                }
              }
            } finally {
              // Clear the session storage flag after a delay to allow for retries
              // if needed (e.g., if initialization failed)
              setTimeout(() => {
                sessionStorage.removeItem(initKey);
              }, 5000);
            }
          }, 100);
        }
      },
      (error) =>
        handleSnapshotError("auth_user_doc", error, (err) => {
          setLinkError(err.message);
        })
    );

    return () => unsubscribe();
  }, [user, initializeUser, claimLink]);

  const value: AuthContextState = {
    user,
    loading,
    isLinked,
    customerId,
    onboardingId,
    linkError,
    isLinking,
    claimLink,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextState {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
